import type { LoggerInterface } from '../logger'
import { LoggerFactory } from '../logger'
import { Type } from '../tools/type'
import { Text } from '../tools/text'
import { Browser } from '../tools/browser'
import { redactSensitiveParams, redactSensitiveUrl, REDACTED_PLACEHOLDER } from '../core/http/redact'
import { StorageManager } from './storage-manager'
import { JsonRpc } from './json-rpc'
import { SdkError } from '../core/sdk-error'
import { SharedConfig } from './shared-config'
import { ChannelManager } from './channel-manager'
import { ResponseBatch, RequestBatch } from './protobuf'
import { decodeResponseBatch, encodeRequestBatch } from './protobuf-lite/messages'
import type { LiteIncomingMessage, LiteReceiver } from './protobuf-lite/messages'
import { CloseReasons, ConnectionType, PullStatus, RpcMethod, SenderType, ServerMode, SubscriptionType, SystemCommands, LsKeys } from '../types/pull'
import { WebSocketConnector } from './web-socket-connector'
import { LongPollingConnector } from './long-polling-connector'
import type { StorageManagerParams, TypePullClientParams, TypePullClientSession, TypeStorageManager, SharedConfigParams, TypeChannelManagerParams, TypeConnector, RpcError, TypePullClientConfig, TypePullMessage, TypeSubscriptionOptions, TypeSubscriptionCommandHandler, TypePullClientEmitConfig, CommandHandlerFunctionV1, CommandHandlerFunctionV2, ConnectorParent, UserStatusCallback, TypePublicIdDescriptor, TypePullClientMessageBatch, TypeChanel, TypeSessionEvent, TypePullClientMessageBody } from '../types/pull'
import type { TypeB24 } from '../types/b24'
import type { AjaxResult } from '../core/http/ajax-result'
import type { SuccessPayload } from '../types/payloads'
import type { NumberString } from '../types/common'

/**
 * @memo api revision - check module/pull/include.php
 */
const REVISION = 19
const RESTORE_WEBSOCKET_TIMEOUT = 30 * 60
const OFFLINE_STATUS_DELAY = 5_000
const CONFIG_CHECK_INTERVAL = 60 * 1000
const MAX_IDS_TO_STORE = 10
const PING_TIMEOUT = 10
const JSON_RPC_PING = 'ping'
const JSON_RPC_PONG = 'pong'
const LS_SESSION = 'bx-pull-session'
const LS_SESSION_CACHE_TIME = 20

const EmptyConfig = {
  api: {},
  channels: {},
  publicChannels: {},
  server: { timeShift: 0 },
  clientId: null,
  jwt: null,
  exp: 0
} as TypePullClientConfig

export class PullClient implements ConnectorParent {
  // region Params ////
  private _logger: LoggerInterface
  private _restClient: TypeB24
  private _status: PullStatus
  private _context: string
  private readonly _guestMode: boolean
  private readonly _guestUserId: number
  private _userId: number

  private _configGetMethod: string
  private _getPublicListMethod: string

  private _siteId: string
  private _enabled: boolean

  private _unloading: boolean = false
  private _starting: boolean = false
  private _debug: boolean = false
  private _connectionAttempt: number = 0
  private _connectionType: ConnectionType = ConnectionType.WebSocket
  private _skipStorageInit: boolean
  private _skipCheckRevision: boolean

  private _subscribers: Record<string, any> = {}
  private _watchTagsQueue: Map<string, boolean> = new Map()
  private _watchUpdateInterval: number = 1_740_000
  private _watchForceUpdateInterval: number = 5_000
  private _configTimestamp: number = 0
  private _session: TypePullClientSession = {
    mid: null,
    tag: null,
    time: null,
    history: {},
    lastMessageIds: [],
    messageCount: 0
  }

  private _connectors: Record<ConnectionType, null | TypeConnector> = {
    [ConnectionType.Undefined]: null,
    [ConnectionType.WebSocket]: null,
    [ConnectionType.LongPolling]: null
  }

  private _isSecure: boolean

  private _config: null | TypePullClientConfig = null

  private _storage: null | TypeStorageManager = null
  private _sharedConfig: SharedConfig
  private _channelManager: ChannelManager
  private _jsonRpcAdapter: null | JsonRpc = null

  /**
   * `true` selects the hand-written codec over the vendored protobuf.js.
   * Off by default. The two agree on well-formed input except for the
   * deliberate divergences recorded in `pull-protobuf.md`, checked by a
   * hand-written and a fuzz differential; the lite codec has very little
   * production exposure, which is the reason the default stays put.
   */
  private _useLiteProtobuf: boolean = false

  /**
   * @depricate
   */
  // private _notificationPopup: null = null

  // timers ////
  private _reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  private _restartTimeout: ReturnType<typeof setTimeout> | null = null
  private _restoreWebSocketTimeout: ReturnType<typeof setTimeout> | null = null
  private _checkInterval: ReturnType<typeof setTimeout> | null = null
  private _offlineTimeout: ReturnType<typeof setTimeout> | null = null
  private _watchUpdateTimeout: ReturnType<typeof setTimeout> | null = null
  private _pingWaitTimeout: ReturnType<typeof setTimeout> | null = null

  // Single source of truth for every tracked timer field. armTimeout()/
  // clearAllTimers() route through this list so a future timer cannot be added
  // without a matching teardown — the leak class behind #141 (#222). Individual
  // fields are kept because call-sites read their identity (e.g. `if
  // (this._reconnectTimeout)`), but clearAllTimers() is the one teardown path.
  private readonly _timerFields = [
    '_reconnectTimeout',
    '_restartTimeout',
    '_restoreWebSocketTimeout',
    '_checkInterval',
    '_offlineTimeout',
    '_watchUpdateTimeout',
    '_pingWaitTimeout'
  ] as const

  // Records every window listener init() registered so removeAllWindowListeners()
  // can drop exactly what was added, keeping arm/teardown symmetric (#222).
  private _windowListeners: Array<{ type: string, handler: () => void }> = []

  // manual stop workaround ////
  private _isManualDisconnect: boolean = false

  // set once destroy() has run; gates reconnect / watch / online so a torn-down
  // client never schedules new work (#141) ////
  private _disposed: boolean = false

  private _loggingEnabled: boolean = false

  // bound event handlers, stored so they can be removed in destroy() (#141) ////
  private _onPingTimeoutHandler: () => void
  private _onBeforeUnloadHandler: () => void
  private _onOfflineHandler: () => void
  private _onOnlineHandler: () => void

  // [userId] => array of callbacks
  private _userStatusCallbacks: Record<number, UserStatusCallback[]> = {}

  private _connectPromise: null | {
    resolve: (response: any) => void
    reject: (error: string | RpcError | Error) => void
  } = null

  private _startingPromise: null | Promise<boolean> = null

  // Monotonic token bumped on every start() and on destroy(). start()'s
  // loadConfig()/connect() continuations capture the value at kick-off and bail
  // if it no longer matches — deterministically neutralising an in-flight start()
  // when destroy() (or a fresh start()) supersedes it, even for overlapping
  // start/destroy/start. The underlying rest transport
  // (this._restClient.actions.v2.call.make) does not accept an AbortSignal, so
  // this generation-token approach is used instead of an AbortController; the
  // network round-trip itself is not cancelled (remaining follow-up). (#222)
  private _startGeneration: number = 0
  // endregion ////

  // region Init ////
  /**
   * @param params
   */
  constructor(params: TypePullClientParams) {
    this._logger = LoggerFactory.createNullLogger()
    this._restClient = params.b24
    this._status = PullStatus.Offline
    this._context = 'master'
    this._useLiteProtobuf = params.protobufCodec === 'lite'

    // region RestApplication ////
    if (params.restApplication) {
      if (typeof params.configGetMethod === 'undefined') {
        params.configGetMethod = 'pull.application.config.get'
      }

      if (typeof params.skipCheckRevision === 'undefined') {
        params.skipCheckRevision = true
      }

      if (Type.isStringFilled(params.restApplication)) {
        params.siteId = params.restApplication
      }

      params.serverEnabled = true
    }
    // endregion ////

    // region Params ////
    this._guestMode = params.guestMode
      ? Text.toBoolean(params.guestMode)
      : false

    this._guestUserId = params.guestUserId
      ? Text.toInteger(params.guestUserId)
      : 0

    if (this._guestMode && this._guestUserId > 0) {
      this._userId = this._guestUserId
    } else {
      this._guestMode = false
      this._userId = params.userId ? Text.toInteger(params.userId) : 0
    }

    this._siteId = params.siteId ?? 'none'

    this._enabled = !Type.isUndefined(params.serverEnabled)
      ? params.serverEnabled === true
      : true

    this._configGetMethod = !Type.isStringFilled(params.configGetMethod)
      ? 'pull.config.get'
      : params.configGetMethod || ''

    this._getPublicListMethod = !Type.isStringFilled(params.getPublicListMethod)
      ? 'pull.channel.public.list'
      : params.getPublicListMethod || ''

    this._skipStorageInit = params.skipStorageInit === true
    this._skipCheckRevision = params.skipCheckRevision === true

    if (!Type.isUndefined(params.configTimestamp)) {
      this._configTimestamp = Text.toInteger(params.configTimestamp)
    }
    // endregion ////

    // Guard the `document` global: under SSR/Node it is undefined and even
    // `document?.` throws a ReferenceError on an undeclared identifier (#222).
    this._isSecure = typeof document !== 'undefined'
      && document?.location.href.indexOf('https') === 0

    if (this._userId && !this._skipStorageInit) {
      this._storage = new StorageManager({
        userId: this._userId,
        siteId: this._siteId
      } as StorageManagerParams)
    }

    this._sharedConfig = new SharedConfig({
      onWebSocketBlockChanged: this.onWebSocketBlockChanged.bind(this),
      storage: this._storage
    } as SharedConfigParams)

    this._channelManager = new ChannelManager({
      b24: this._restClient,
      getPublicListMethod: this._getPublicListMethod
    } as TypeChannelManagerParams)

    this._loggingEnabled = this._sharedConfig.isLoggingEnabled()

    // bound event handlers ////
    this._onPingTimeoutHandler = this.onPingTimeout.bind(this)
    this._onBeforeUnloadHandler = this.onBeforeUnload.bind(this)
    this._onOfflineHandler = this.onOffline.bind(this)
    this._onOnlineHandler = this.onOnline.bind(this)
  }

  setLogger(logger: LoggerInterface): void {
    this._logger = logger
    this._jsonRpcAdapter?.setLogger(this.getLogger())
    this._storage?.setLogger(this.getLogger())
    this._sharedConfig.setLogger(this.getLogger())
    this._channelManager.setLogger(this.getLogger())

    this._connectors.webSocket?.setLogger(this.getLogger())
    this._connectors.longPolling?.setLogger(this.getLogger())
  }

  getLogger(): LoggerInterface {
    return this._logger
  }

  /**
   * Terminal teardown: removes the window listeners, cancels every pending timer,
   * persists the session for a quick re-init, and disconnects. Irreversible — a
   * destroyed client schedules no further work and `start()` rejects with
   * `PULL_DISPOSED`; create a new instance to reconnect.
   */
  destroy(): void {
    this._disposed = true

    // Invalidate any in-flight start() continuation (#222).
    this._startGeneration++

    this.stop(CloseReasons.NORMAL_CLOSURE, 'manual stop')

    this.removeAllWindowListeners()

    // Drop the cached pull config on teardown: it holds the push `jwt` and the
    // channel signatures, which must not linger in localStorage after the client
    // is gone (it is readable by any same-origin script and survives reloads). A
    // torn-down client is terminal; a fresh instance re-fetches the config (#242).
    if (this._storage) {
      this._storage.remove(LsKeys.PullConfig)
    }

    // Save the (non-secret) session for a quick re-init, but — unlike
    // onBeforeUnload — do NOT schedule a reconnect: the client is being torn
    // down (#141).
    this.persistSession()
  }

  private init(): void {
    if (this._disposed) {
      return
    }

    this._connectors.webSocket = new WebSocketConnector({
      parent: this,
      onOpen: this.onWebSocketOpen.bind(this),
      onMessage: this.onIncomingMessage.bind(this),
      onDisconnect: this.onWebSocketDisconnect.bind(this),
      onError: this.onWebSocketError.bind(this)
    })

    this._connectors.longPolling = new LongPollingConnector({
      parent: this,
      onOpen: this.onLongPollingOpen.bind(this),
      onMessage: this.onIncomingMessage.bind(this),
      onDisconnect: this.onLongPollingDisconnect.bind(this),
      onError: this.onLongPollingError.bind(this)
    })

    this._connectionType = this.isWebSocketAllowed()
      ? ConnectionType.WebSocket
      : ConnectionType.LongPolling

    this.addWindowListener('beforeunload', this._onBeforeUnloadHandler)
    this.addWindowListener('offline', this._onOfflineHandler)
    this.addWindowListener('online', this._onOnlineHandler)

    /**
     * @memo Not use under Node.js
     */
    /* /
    if (BX && BX.addCustomEvent)
    {
      BX.addCustomEvent('BXLinkOpened', this.connect.bind(this))
    }

    if (BX && BX.desktop)
    {
      BX.addCustomEvent('onDesktopReload', () => {
        this._session.mid = null
        this._session.tag = null
        this._session.time = null
      })

      BX.desktop.addCustomEvent('BXLoginSuccess', () => this.restart(1_000, 'desktop login'))
    }
    // */

    this._jsonRpcAdapter = new JsonRpc({
      connector: this._connectors.webSocket,
      handlers: {
        'incoming.message': this.handleRpcIncomingMessage.bind(this)
      }
    })
  }

  // endregion ////

  // region Get-Set ////
  get connector(): null | TypeConnector {
    return this._connectors[this._connectionType]
  }

  get status(): PullStatus {
    return this._status
  }

  /**
   * @param status
   */
  set status(status: PullStatus) {
    if (this._status === status) {
      return
    }

    this._status = status
    if (this._offlineTimeout) {
      clearTimeout(this._offlineTimeout)
      this._offlineTimeout = null
    }

    if (status === PullStatus.Offline) {
      this.sendPullStatusDelayed(status, OFFLINE_STATUS_DELAY)
    } else {
      this.sendPullStatus(status)
    }
  }

  get session(): TypePullClientSession {
    return this._session
  }

  // endregion ////

  // region Public /////
  /**
   * Creates a subscription to incoming messages.
   *
   * @param {TypeSubscriptionOptions | TypeSubscriptionCommandHandler} params
   * @returns { () => void } - Unsubscribe callback function
   */
  public subscribe(
    params: TypeSubscriptionOptions | TypeSubscriptionCommandHandler
  ): () => void {
    if (!Type.isPlainObject(params)) {
      return this.attachCommandHandler(params as TypeSubscriptionCommandHandler)
    }

    params = params as TypeSubscriptionOptions

    params.type = params.type || SubscriptionType.Server
    params.command = params.command || null

    if (
      params.type == SubscriptionType.Server
      || params.type == SubscriptionType.Client
    ) {
      if (typeof params.moduleId === 'undefined') {
        throw new TypeError(
          `${Text.getDateForLog()}: Pull.subscribe: parameter moduleId is not specified`
        )
      }

      if (typeof this._subscribers[params.type] === 'undefined') {
        this._subscribers[params.type] = {}
      }

      if (
        typeof this._subscribers[params.type][params.moduleId] === 'undefined'
      ) {
        this._subscribers[params.type][params.moduleId] = {
          callbacks: [],
          commands: {}
        }
      }

      if (params.command) {
        if (
          typeof this._subscribers[params.type][params.moduleId]['commands'][
            params.command
          ] === 'undefined'
        ) {
          this._subscribers[params.type][params.moduleId]['commands'][
            params.command
          ] = []
        }

        this._subscribers[params.type][params.moduleId]['commands'][
          params.command
        ].push(params.callback)

        return () => {
          if (
            typeof params.type === 'undefined'
            || typeof params.moduleId === 'undefined'
            || typeof params.command === 'undefined'
            || null === params.command
          ) {
            return
          }

          this._subscribers[params.type][params.moduleId]['commands'][
            params.command
          ] = this._subscribers[params.type][params.moduleId]['commands'][
            params.command
          ].filter((element: any) => {
            return element !== params.callback
          })
        }
      } else {
        this._subscribers[params.type][params.moduleId]['callbacks'].push(
          params.callback
        )

        return () => {
          if (
            typeof params.type === 'undefined'
            || typeof params.moduleId === 'undefined'
          ) {
            return
          }

          this._subscribers[params.type][params.moduleId]['callbacks']
            = this._subscribers[params.type][params.moduleId]['callbacks'].filter(
              (element: any) => {
                return element !== params.callback
              }
            )
        }
      }
    } else {
      if (typeof this._subscribers[params.type] === 'undefined') {
        this._subscribers[params.type] = []
      }

      this._subscribers[params.type].push(params.callback)

      return () => {
        if (typeof params.type === 'undefined') {
          return
        }

        this._subscribers[params.type] = this._subscribers[params.type].filter(
          (element: any) => {
            return element !== params.callback
          }
        )
      }
    }
  }

  /**
   * @param {TypeSubscriptionCommandHandler} handler
   * @returns {() => void} - Unsubscribe callback function
   */
  private attachCommandHandler(
    handler: TypeSubscriptionCommandHandler
  ): () => void {
    if (
      typeof handler.getModuleId !== 'function'
      || typeof handler.getModuleId() !== 'string'
    ) {
      this.getLogger().error(`${Text.getDateForLog()}: Pull.attachCommandHandler: result of handler.getModuleId() is not a string.`).catch(() => {})
      return () => {}
    }

    let type = SubscriptionType.Server
    if (typeof handler.getSubscriptionType === 'function') {
      type = handler.getSubscriptionType()
    }

    return this.subscribe({
      type: type,
      moduleId: handler.getModuleId(),
      callback: (data: TypePullMessage) => {
        let method = null

        if (typeof handler.getMap === 'function') {
          const mapping = handler.getMap()
          if (mapping && typeof mapping === 'object') {
            const rowMapping = mapping[data.command]
            if (typeof rowMapping === 'function') {
              method = rowMapping.bind(handler)
            } else if (
              typeof rowMapping === 'string'
              && typeof handler[rowMapping] === 'function'
            ) {
              method = handler[rowMapping].bind(handler)
            }
          }
        }

        /**
         * handler.handleSomeCommandName: CommandHandlerFunction
         */
        if (!method) {
          const methodName = `handle${Text.capitalize(data.command)}`
          if (typeof handler[methodName] === 'function') {
            method = handler[methodName].bind(handler)
          }
        }

        if (method) {
          if (this._debug && this._context !== 'master') {
            this.getLogger().warning(
              `${Text.getDateForLog()}: Pull.attachCommandHandler: result of handler.getModuleId() is not a string`,
              // data.params / data.extra are app-defined and may carry a credential key (#43)
              redactSensitiveParams({ data } as unknown as Record<string, unknown>)
            ).catch(() => {})
          }

          method(data.params, data.extra, data.command)
        }
      }
    })
  }

  /**
   * Connects the client and begins receiving events.
   *
   * @param config
   * @throws Rejects with `{ ex: { error: 'PULL_DISPOSED' } }` when called after
   *   `destroy()` — a destroyed client cannot be restarted; create a new instance.
   */
  public async start(
    config:
      | null
      | (TypePullClientConfig & {
        skipReconnectToLastSession?: boolean
      }) = null
  ): Promise<boolean> {
    if (this._disposed) {
      return Promise.reject({
        ex: {
          error: 'PULL_DISPOSED',
          error_description: 'PullClient has been destroyed; create a new instance'
        }
      })
    }

    let allowConfigCaching = true

    if (this.isConnected()) {
      return Promise.resolve(true)
    }

    if (this._starting && this._startingPromise) {
      return this._startingPromise
    }

    if (!this._userId) {
      throw new Error('Not set userId')
    }

    if (this._siteId === 'none') {
      throw new Error('Not set siteId')
    }

    let skipReconnectToLastSession = false
    if (!!config && Type.isPlainObject(config)) {
      if (typeof config?.skipReconnectToLastSession !== 'undefined') {
        skipReconnectToLastSession = config.skipReconnectToLastSession
        delete config.skipReconnectToLastSession
      }

      this._config = config
      allowConfigCaching = false
    }

    if (!this._enabled) {
      return Promise.reject({
        ex: {
          error: 'PULL_DISABLED',
          error_description: 'Push & Pull server is disabled'
        }
      })
    }

    const now = Date.now()
    let oldSession
    if (!skipReconnectToLastSession && this._storage) {
      oldSession = this._storage.get(LS_SESSION, null)
    }

    if (
      Type.isPlainObject(oldSession)
      && Object.prototype.hasOwnProperty.call(oldSession, 'ttl')
      && oldSession['ttl'] >= now
    ) {
      this._session.mid = oldSession['mid']
    }

    this._starting = true
    // Capture the current generation; a destroy() or a newer start() bumps
    // _startGeneration and the continuation below bails (#222).
    const startGeneration = ++this._startGeneration
    return (this._startingPromise = new Promise((resolve, reject) => {
      this.loadConfig('client_start')
        .then((config) => {
          // Abort the continuation if the client was destroyed or superseded
          // while loadConfig()'s network round-trip was in flight. The
          // _disposed guards inside setConfig/init/connect are a second line of
          // defence; this bails deterministically and rejects the start (#222).
          if (this._disposed || this._startGeneration !== startGeneration) {
            this._starting = false
            reject({
              ex: {
                error: 'PULL_DISPOSED',
                error_description: 'PullClient has been destroyed; create a new instance'
              }
            })
            return
          }

          this.setConfig(config as TypePullClientConfig, allowConfigCaching)
          this.init()
          this.updateWatch(true)
          this.startCheckConfig()

          this.connect().then(
            () => resolve(true),
            error => reject(error)
          )
        })
        .catch((error) => {
          this._starting = false
          this.status = PullStatus.Offline
          this.stopCheckConfig()
          this.getLogger().error(
            `${Text.getDateForLog()}: Pull: could not read push-server config`,
            { error }
          ).catch(() => {})
          reject(error)
        })
    }))
  }

  /**
   * @param disconnectCode
   * @param disconnectReason
   */
  public restart(
    disconnectCode: number | CloseReasons = CloseReasons.NORMAL_CLOSURE,
    disconnectReason: string = 'manual restart'
  ): void {
    if (this._disposed) {
      return
    }

    if (this._restartTimeout) {
      clearTimeout(this._restartTimeout)
      this._restartTimeout = null
    }

    this.getLogger().debug(
      `${Text.getDateForLog()}: Pull: restarting with code ${disconnectCode}`
    ).catch(() => {})

    this.disconnect(disconnectCode, disconnectReason)

    if (this._storage) {
      this._storage.remove(LsKeys.PullConfig)
    }

    this._config = null

    const loadConfigReason = `${disconnectCode}_${disconnectReason.replaceAll(' ', '_')}`
    this.loadConfig(loadConfigReason).then(
      (config) => {
        this.setConfig(config, true)
        this.updateWatch()
        this.startCheckConfig()
        this.connect().catch((error) => {
          this.getLogger().error('restart error', { error }).catch(() => {})
        })
      },
      (error) => {
        this.getLogger().error(
          `${Text.getDateForLog()}: Pull: could not read push-server config `,
          { error }
        ).catch(() => {})

        this.status = PullStatus.Offline
        if (this._reconnectTimeout) {
          clearTimeout(this._reconnectTimeout)
          this._reconnectTimeout = null
        }

        if (error?.status == 401 || error?.status == 403) {
          this.stopCheckConfig()
          this.onCustomEvent('onPullError', ['AUTHORIZE_ERROR'])
        }
      }
    )
  }

  public stop(
    disconnectCode: number | CloseReasons = CloseReasons.NORMAL_CLOSURE,
    disconnectReason: string = 'manual stop'
  ): void {
    this.disconnect(disconnectCode, disconnectReason)

    this.stopCheckConfig()

    this.clearAllTimers()
  }

  // Cancel every pending timer so a stopped/destroyed client leaves nothing
  // running. Previously only _checkInterval was cleared, so the other six timers
  // (including the self-rescheduling watch-extend) survived teardown (#141).
  private clearAllTimers(): void {
    for (const field of this._timerFields) {
      const timer = this[field]
      if (timer) {
        if (field === '_checkInterval') {
          clearInterval(timer)
        } else {
          clearTimeout(timer)
        }
      }
      this[field] = null
    }
  }

  // Arm a tracked setTimeout: clears any existing id for the field first (same
  // clear-before-set the call-sites did by hand), then stores the new id. The
  // interval-backed _checkInterval keeps its own setInterval path. (#222)
  private armTimeout(
    field: Exclude<(typeof this._timerFields)[number], '_checkInterval'>,
    fn: () => void,
    ms: number
  ): void {
    const existing = this[field]
    if (existing) {
      clearTimeout(existing)
    }
    this[field] = setTimeout(fn, ms)
  }

  // Window-listener registry: init() adds through here, destroy() drops through
  // removeAllWindowListeners(), so a listener can't be added without teardown. (#222)
  private addWindowListener(type: string, handler: () => void): void {
    if (typeof window === 'undefined') {
      return
    }
    window.addEventListener(type, handler)
    this._windowListeners.push({ type, handler })
  }

  private removeAllWindowListeners(): void {
    if (typeof window !== 'undefined') {
      for (const { type, handler } of this._windowListeners) {
        window.removeEventListener(type, handler)
      }
    }
    this._windowListeners = []
  }

  public reconnect(
    disconnectCode: number | CloseReasons,
    disconnectReason: string,
    delay: number = 1
  ): void {
    this.disconnect(disconnectCode, disconnectReason)

    this.scheduleReconnect(delay)
  }

  /**
   * @param lastMessageId
   */
  public setLastMessageId(lastMessageId: string): void {
    this._session.mid = lastMessageId
  }

  /**
   * Send a single message to the specified users.
   *
   * **Not the supported path in an application.** Resolving the recipients'
   * channels needs `pull.channel.public.list`, which is not part of the
   * application REST surface, and an application's Pull client is documented
   * as receive-only, with its back end publishing through
   * `pull.application.event.add`. See
   * {@link https://apidocs.bitrix24.ru/api-reference/interactivity/push-and-pull-in-browser.html}.
   *
   * Rejects with `JSSDK_PULL_PUBLIC_IDS_UNAVAILABLE` when that lookup fails.
   * Until 3.0.0 it resolved instead, and the message was dropped by the push
   * server without a word — so a caller with no `catch` sees a rejection where
   * it previously saw a silent no-op.
   *
   * "Not supported" rather than "impossible", and the difference is worth
   * knowing before treating one outcome as the only one. The lookup is skipped
   * entirely when every recipient already has an unexpired channel in
   * `ChannelManager`'s cache, which the startup config call prefills from
   * `publicChannels` — so a send addressed to the CURRENT USER frequently goes
   * out with no lookup and no rejection. It is still not a delivery receipt,
   * and a message published this way comes back to subscribers of
   * {@link SubscriptionType.Client}, not of `Server`.
   *
   * @param users User ids of the message receivers.
   * @param moduleId Name of the module to receive a message,
   * @param command Command name.
   * @param {object} params Command parameters.
   * @param [expiry] Message expiry time in seconds.
   * @return {Promise<true>} resolves only once the transport has ACCEPTED the
   *   frame. That is not a delivery receipt: long-polling fires its request
   *   without awaiting the response, so an accepted frame can still be lost.
   * @throws {SdkError} `JSSDK_PULL_PUBLIC_IDS_UNAVAILABLE` — nobody to send to.
   * @throws {SdkError} `JSSDK_PULL_SEND_REFUSED` — the transport would not take
   *   the frame (socket closed, no publication path).
   * @throws {SdkError} `JSSDK_PULL_PUBLISHING_DISABLED` — the portal has not
   *   enabled client publishing.
   */
  public async sendMessage(
    users: number[],
    moduleId: string,
    command: string,
    params: any,
    expiry?: number
  ): Promise<any> {
    const message = {
      userList: users,
      body: {
        module_id: moduleId,
        command: command,
        params: params
      },
      expiry: expiry
    } as TypePullClientMessageBatch

    if (this.isJsonRpc()) {
      return this.requireJsonRpcAdapter().executeOutgoingRpcCommand(
        RpcMethod.Publish,
        message
      )
    } else {
      return this.sendMessageBatch([message])
    }
  }

  /**
   * Send a single message to the specified public channels.
   *
   * Unlike {@link sendMessage}, this does **not** resolve anything through
   * `pull.channel.public.list` — the caller supplies signed channel ids — so it
   * cannot raise `JSSDK_PULL_PUBLIC_IDS_UNAVAILABLE` and is unaffected by that
   * method's absence from the application REST surface.
   *
   * It does share the other two failures: it rejects with
   * `JSSDK_PULL_SEND_REFUSED` when the transport will not take the frame, and
   * with `JSSDK_PULL_PUBLISHING_DISABLED` when the portal has not enabled
   * client publishing. Both used to resolve as though the message had been
   * sent.
   *
   * @param  publicChannels Public ids of the channels to receive a message.
   * @param moduleId Name of the module to receive a message,
   * @param command Command name.
   * @param {object} params Command parameters.
   * @param [expiry] Message expiry time in seconds.
   * @return {Promise}
   */
  public async sendMessageToChannels(
    publicChannels: string[],
    moduleId: string,
    command: string,
    params: any,
    expiry?: number
  ): Promise<any> {
    const message = {
      channelList: publicChannels,
      body: {
        module_id: moduleId,
        command: command,
        params: params
      },
      expiry: expiry
    } as TypePullClientMessageBatch

    if (this.isJsonRpc()) {
      return this.requireJsonRpcAdapter().executeOutgoingRpcCommand(
        RpcMethod.Publish,
        message
      )
    } else {
      return this.sendMessageBatch([message])
    }
  }

  /**
   * @param debugFlag
   */
  public capturePullEvent(debugFlag: boolean = true): void {
    this._debug = debugFlag
  }

  /**
   * @param loggingFlag
   */
  public enableLogging(loggingFlag: boolean = true): void {
    this._sharedConfig.setLoggingEnabled(loggingFlag)
    this._loggingEnabled = loggingFlag
  }

  /**
   * Returns list channels that the connection is subscribed to.
   *
   * @returns {Promise}
   */
  public async listChannels(): Promise<any> {
    return (
      this._jsonRpcAdapter?.executeOutgoingRpcCommand(
        RpcMethod.ListChannels,
        {}
      ) || Promise.reject(new Error('jsonRpcAdapter not init'))
    )
  }

  /**
   * Returns "last seen" time in seconds for the users.
   * Result format: Object{userId: int}
   * If the user is currently connected - will return 0.
   * If the user is offline - will return the diff between the current timestamp and the last seen timestamp in seconds.
   * If the user was never online - the record for the user will be missing from the result object.
   *
   * @param {integer[]} userList List of user ids.
   * @returns {Promise}
   */
  public async getUsersLastSeen(
    userList: number[]
  ): Promise<Record<number, number>> {
    if (
      !Type.isArray(userList)
      || !userList.every(item => typeof item === 'number')
    ) {
      throw new Error('userList must be an array of numbers')
    }

    const result: Record<number, number> = {}

    return new Promise((resolve, reject) => {
      this._jsonRpcAdapter
        ?.executeOutgoingRpcCommand(RpcMethod.GetUsersLastSeen, {
          userList: userList
        })
        .then((response: any) => {
          const unresolved = []

          for (let i = 0; i < userList.length; i++) {
            if (!Object.prototype.hasOwnProperty.call(response, userList[i]!)) {
              unresolved.push(userList[i])
            }
          }

          if (unresolved.length === 0) {
            return resolve(result)
          }

          const params = {
            userIds: unresolved,
            sendToQueueSever: true
          }

          this._restClient
            .actions.v2.call.make({
              method: 'pull.api.user.getLastSeen',
              params
            })
            .then((response: AjaxResult) => {
              const data = (response.getData() as SuccessPayload<Record<NumberString, NumberString>>).result as any
              for (const userId in data) {
                result[Number(userId)] = Number(data[userId])
              }

              return resolve(result)
            })
            .catch((error) => {
              this.getLogger().error('getUsersLastSeen', { error }).catch(() => {})
              reject(error)
            })
        })
        .catch((error) => {
          this.getLogger().error('getUsersLastSeen', { error }).catch(() => {})
          reject(error)
        })
    })
  }

  /**
   * Pings server.
   * In case of success promise will be resolved, otherwise - rejected.
   *
   * @param {number} timeout Request timeout in seconds
   * @returns {Promise}
   */
  public async ping(timeout: number = 5): Promise<void> {
    return this._jsonRpcAdapter?.executeOutgoingRpcCommand(
      RpcMethod.Ping,
      {},
      timeout
    )
  }

  /**
   * @param userId {number}
   * @param callback {UserStatusCallback}
   * @returns {Promise}
   */
  public async subscribeUserStatusChange(
    userId: number,
    callback: UserStatusCallback
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this._jsonRpcAdapter
        ?.executeOutgoingRpcCommand(RpcMethod.SubscribeStatusChange, {
          userId
        })
        .then(() => {
          if (!this._userStatusCallbacks[userId]) {
            this._userStatusCallbacks[userId] = []
          }

          if (Type.isFunction(callback)) {
            this._userStatusCallbacks[userId].push(callback)
          }

          return resolve()
        })
        .catch(error => reject(error))
    })
  }

  /**
   * @param {number} userId
   * @param {UserStatusCallback} callback
   * @returns {Promise}
   */
  public async unsubscribeUserStatusChange(
    userId: number,
    callback: UserStatusCallback
  ): Promise<void> {
    if (this._userStatusCallbacks[userId]) {
      this._userStatusCallbacks[userId] = this._userStatusCallbacks[
        userId
      ].filter(cb => cb !== callback)

      if (this._userStatusCallbacks[userId].length === 0) {
        return this._jsonRpcAdapter?.executeOutgoingRpcCommand(
          RpcMethod.UnsubscribeStatusChange,
          {
            userId
          }
        )
      }
    }

    return Promise.resolve()
  }

  // endregion ////

  // region Get ////
  public getRevision(): number | null {
    return this._config && this._config.api
      ? this._config.api.revision_web
      : null
  }

  public getServerVersion(): number {
    return this._config && this._config.server ? this._config.server.version : 0
  }

  public getServerMode(): string | null {
    return this._config && this._config.server ? this._config.server.mode : null
  }

  public getConfig(): null | TypePullClientConfig {
    return this._config
  }

  public getDebugInfo(): any {
    if (!JSON || !JSON.stringify) {
      return {}
    }

    let configDump
    if (this._config && this._config.channels) {
      configDump = {
        // The private channel id is a subscription secret — mask it here too, not
        // just in `Path` below, so the debug dump never surfaces it (#148). Expiry
        // timestamps are non-sensitive and kept for diagnostics.
        ChannelID: this._config.channels.private?.id ? REDACTED_PLACEHOLDER : 'n/a',
        ChannelDie: this._config.channels.private?.end || 'n/a',
        ChannelDieShared: this._config.channels.shared?.end || 'n/a'
      }
    } else {
      configDump = {
        ConfigError: 'config is not loaded'
      }
    }

    let websocketMode = '-'
    if (
      this._connectors.webSocket
      && (this._connectors.webSocket as WebSocketConnector)?.socket
    ) {
      if (this.isJsonRpc()) {
        websocketMode = 'json-rpc'
      } else {
        websocketMode

          = (
            this._connectors.webSocket as WebSocketConnector
          )?.socket?.url.search('binaryMode=true') != -1
            ? 'protobuf'
            : 'text'
      }
    }

    const connectionPath = this.connector?.connectionPath

    return {
      'UserId': this._userId + (this._userId > 0 ? '' : '(guest)'),
      'Guest userId':
        this._guestMode && this._guestUserId !== 0 ? this._guestUserId : '-',
      'Browser online': (typeof navigator !== 'undefined' && navigator.onLine) ? 'Y' : 'N',
      'Connect': this.isConnected() ? 'Y' : 'N',
      'Server type': this.isSharedMode() ? 'cloud' : 'local',
      'WebSocket supported': this.isWebSocketSupported() ? 'Y' : 'N',
      'WebSocket connected':
        this._connectors.webSocket && this._connectors.webSocket.connected
          ? 'Y'
          : 'N',
      'WebSocket mode': websocketMode,

      'Try connect': this._reconnectTimeout ? 'Y' : 'N',
      'Try number': this._connectionAttempt,

      // Mask the push JWT (`token`) and private `CHANNEL_ID`s before exposing
      // the connection path through this developer-facing debug dump (#148).
      'Path': connectionPath
        ? redactSensitiveUrl(connectionPath, ['CHANNEL_ID'])
        : '-',
      ...configDump,

      'Last message': this._session.mid || '-',
      'Session history': this._session.history,
      'Watch tags': this._watchTagsQueue.entries()
    }
  }

  /**
   * @process
   * @param connectionType
   */
  public getConnectionPath(connectionType: ConnectionType): string {
    let path
    const params: any = {}

    switch (connectionType) {
      case ConnectionType.WebSocket:
        path = this._isSecure
          ? this._config?.server.websocket_secure
          : this._config?.server.websocket
        break
      case ConnectionType.LongPolling:
        path = this._isSecure
          ? this._config?.server.long_pooling_secure
          : this._config?.server.long_polling
        break
      default:
        throw new Error(`Unknown connection type ${connectionType}`)
    }

    if (!Type.isStringFilled(path)) {
      throw new Error(`Empty path`)
    }

    if (typeof this._config?.jwt === 'string' && this._config?.jwt !== '') {
      params['token'] = this._config?.jwt
    } else {
      const channels: string[] = []

      // One guarded push per channel — previously the private id was pushed
      // twice (and an empty segment when `private` existed without an `id`),
      // producing a malformed `CHANNEL_ID` like `id/id` or a leading `/` (#238).
      if (this._config?.channels?.private?.id) {
        channels.push(this._config.channels.private.id)
      }

      if (this._config?.channels?.shared?.id) {
        channels.push(this._config.channels.shared.id)
      }

      if (channels.length === 0) {
        throw new Error(`Empty channels`)
      }

      params['CHANNEL_ID'] = channels.join('/')
    }

    if (this.isJsonRpc()) {
      params.jsonRpc = 'true'
    } else if (this.isProtobufSupported()) {
      params.binaryMode = 'true'
    }

    if (this.isSharedMode()) {
      if (!this._config?.clientId) {
        throw new Error(
          'Push-server is in shared mode, but clientId is not set'
        )
      }

      params.clientId = this._config.clientId
    }
    if (this._session.mid) {
      params.mid = this._session.mid
    }
    if (this._session.tag) {
      params.tag = this._session.tag
    }
    if (this._session.time) {
      params.time = this._session.time
    }
    params.revision = REVISION

    return `${path}?${Text.buildQueryString(params)}`
  }

  /**
   * @process
   */
  public getPublicationPath(): string {
    const path = this._isSecure
      ? this._config?.server.publish_secure
      : this._config?.server.publish

    if (!path) {
      return ''
    }

    const channels: string[] = []

    if (this._config?.channels.private?.id) {
      channels.push(this._config.channels.private.id)
    }

    if (this._config?.channels.shared?.id) {
      channels.push(this._config.channels.shared.id)
    }

    const params = {
      CHANNEL_ID: channels.join('/')
    }

    return path + '?' + Text.buildQueryString(params)
  }

  // endregion ////

  // region Is* ////
  public isConnected(): boolean {
    return this.connector ? this.connector.connected : false
  }

  public isWebSocketSupported(): boolean {
    return typeof window !== 'undefined' && typeof window.WebSocket !== 'undefined'
  }

  public isWebSocketAllowed(): boolean {
    if (this._sharedConfig.isWebSocketBlocked()) {
      return false
    }

    return this.isWebSocketEnabled()
  }

  public isWebSocketEnabled(): boolean {
    if (!this.isWebSocketSupported()) {
      return false
    }

    if (!this._config) {
      return false
    }

    if (!this._config.server) {
      return false
    }

    return this._config.server.websocket_enabled
  }

  public isPublishingSupported(): boolean {
    return this.getServerVersion() > 3
  }

  public isPublishingEnabled(): boolean {
    if (!this.isPublishingSupported()) {
      return false
    }

    return this._config?.server.publish_enabled === true
  }

  public isProtobufSupported(): boolean {
    return this.getServerVersion() == 4 && !Browser.isIE()
  }

  public isJsonRpc(): boolean {
    return this.getServerVersion() >= 5
  }

  public isSharedMode(): boolean {
    return this.getServerMode() === ServerMode.Shared
  }

  // endregion ////

  // region Events ////
  /**
   * @param {TypePullClientEmitConfig} params
   * @returns {boolean}
   */
  private emit(params: TypePullClientEmitConfig): boolean {
    if (
      params.type == SubscriptionType.Server
      || params.type == SubscriptionType.Client
    ) {
      if (typeof this._subscribers[params.type] === 'undefined') {
        this._subscribers[params.type] = {}
      }

      if (typeof params.moduleId === 'undefined') {
        throw new TypeError(
          `${Text.getDateForLog()}: Pull.emit: parameter moduleId is not specified`
        )
      }

      if (
        typeof this._subscribers[params.type][params.moduleId] === 'undefined'
      ) {
        this._subscribers[params.type][params.moduleId] = {
          callbacks: [],
          commands: {}
        }
      }

      if (
        this._subscribers[params.type][params.moduleId]['callbacks'].length > 0
      ) {
        this._subscribers[params.type][params.moduleId]['callbacks'].forEach(
          (callback: CommandHandlerFunctionV1) => {
            callback(params.data as Record<string, any>, {
              type: params.type,
              moduleId: params.moduleId ?? '?'
            })
          }
        )
      }

      if (
        !(typeof params.data === 'undefined')
        && !(typeof params.data['command'] === 'undefined')
        && this._subscribers[params.type][params.moduleId]['commands'][
          params.data['command']
        ]
        && this._subscribers[params.type][params.moduleId]['commands'][
          params.data['command']
        ].length > 0
      ) {
        this._subscribers[params.type][params.moduleId]['commands'][
          params.data['command']

        ].forEach((callback: CommandHandlerFunctionV2) => {
          if (typeof params.data === 'undefined') {
            return
          }

          callback(
            params.data['params'],
            params.data['extra'],
            params.data['command'],
            {
              type: params.type,
              moduleId: params.moduleId as string
            }
          )
        })
      }

      return true
    } else {
      if (typeof this._subscribers[params.type] === 'undefined') {
        this._subscribers[params.type] = []
      }

      if (this._subscribers[params.type].length <= 0) {
        return true
      }

      this._subscribers[params.type].forEach(
        (callback: CommandHandlerFunctionV1) => {
          callback(params.data as Record<string, any>, {
            type: params.type
          })
        }
      )

      return true
    }
  }

  /**
   * @process
   *
   * @param message
   */
  private broadcastMessage(message: TypePullClientMessageBody): void {
    const moduleId = (message.module_id = message.module_id.toLowerCase())
    const command = message.command

    if (!message.extra) {
      message.extra = {}
    }

    if (message.extra.server_time_unix) {
      message.extra.server_time_ago
        = (Date.now() - message.extra.server_time_unix * 1000) / 1000
          - (this._config?.server.timeShift || 0)
      message.extra.server_time_ago = Math.max(message.extra.server_time_ago, 0)
    }

    this.logMessage(message)
    try {
      if (
        message.extra.sender
        && message.extra.sender.type === SenderType.Client
      ) {
        this.onCustomEvent(
          'onPullClientEvent-' + moduleId,
          [command, message.params, message.extra],
          true
        )
        this.onCustomEvent(
          'onPullClientEvent',
          [moduleId, command, message.params, message.extra],
          true
        )

        this.emit({
          type: SubscriptionType.Client,
          moduleId: moduleId,
          data: {
            command: command,
            params: Type.clone(message.params),
            extra: Type.clone(message.extra)
          }
        })
      } else if (moduleId === 'pull') {
        this.handleInternalPullEvent(command, message)
      } else if (moduleId == 'online') {
        if ((message?.extra?.server_time_ago || 0) < 240) {
          this.onCustomEvent(
            'onPullOnlineEvent',
            [command, message.params, message.extra],
            true
          )

          this.emit({
            type: SubscriptionType.Online,
            data: {
              command: command,
              params: Type.clone(message.params),
              extra: Type.clone(message.extra)
            }
          })
        }

        if (command === 'userStatusChange') {
          this.emitUserStatusChange(
            message.params.user_id,
            message.params.online
          )
        }
      } else {
        this.onCustomEvent(
          'onPullEvent-' + moduleId,
          [command, message.params, message.extra],
          true
        )
        this.onCustomEvent(
          'onPullEvent',
          [moduleId, command, message.params, message.extra],
          true
        )

        this.emit({
          type: SubscriptionType.Server,
          moduleId: moduleId,
          data: {
            command: command,
            params: Type.clone(message.params),
            extra: Type.clone(message.extra)
          }
        })
      }
    } catch (error) {
      this.getLogger().warning('PULL ERROR', {
        errorType: 'broadcastMessages execute error',
        errorEvent: error,
        // app-defined message.params / extra may carry a credential key (#43)
        message: redactSensitiveParams(message as unknown as Record<string, unknown>)
      }).catch(() => {})
    }

    if (message.extra && message.extra.revision_web) {
      this.checkRevision(Text.toInteger(message.extra.revision_web))
    }
  }

  /**
   * @process
   *
   * @param messages
   */
  private broadcastMessages(messages: TypePullClientMessageBody[]): void {
    for (const message of messages) {
      this.broadcastMessage(message)
    }
  }

  // endregion ////

  // region sendMessage ////
  /**
   * Sends batch of messages to the multiple public channels.
   *
   * @param messageBatchList Array of messages to send.
   * @return void
   */
  private async sendMessageBatch(
    messageBatchList: TypePullClientMessageBatch[]
  ): Promise<any> {
    if (!this.isPublishingEnabled()) {
      this.getLogger().error(`Client publishing is not supported or is disabled`).catch(() => {})

      return Promise.reject(new SdkError({
        code: 'JSSDK_PULL_PUBLISHING_DISABLED',
        description: 'Pull: this portal has not enabled client publishing, so the message cannot be sent. Publish from your back end with `pull.application.event.add` instead.',
        status: 0
      }))
    }

    if (this.isJsonRpc()) {
      // Not `?.`: `JSON.stringify(undefined)` returns `undefined`, which the
      // connector accepts and puts on the wire as the string "undefined" while
      // this method resolves `true`. A publish that never happened, reported
      // as a success — one layer further out than the defect 3.0.0 fixed.
      const rpcRequest = this.requireJsonRpcAdapter().createPublishRequest(messageBatchList)

      // Same rule as the protobuf branch below: a refused frame is a failure,
      // not a resolved promise. This used to drop the connector's `false` and
      // resolve `true` unconditionally.
      return Promise.resolve(this.handOverToConnector(JSON.stringify(rpcRequest)))
    } else {
      const userIds: Record<number, number> = {}

      for (const messageBatch of messageBatchList) {
        if (typeof messageBatch.userList !== 'undefined') {
          for (const user of messageBatch.userList) {
            const userId = Number(user)
            userIds[userId] = userId
          }
        }
      }

      // The promise is RETURNED. It used to be started and dropped, so this
      // method resolved `undefined` before the channel lookup had even
      // answered — `sendMessage()` reported success while the send was still
      // pending, and any failure inside became an unhandled rejection. Measured
      // against a live portal: the caller was told the message was accepted and
      // nothing ever arrived.
      // No `!this._channelManager` guard: the field is non-optional and the
      // constructor assigns it unconditionally, so a guard here would be a
      // bare `Error` on an unreachable branch — which the checklist forbids
      // and no caller could ever receive.
      return this._channelManager
        .getPublicIds(Object.values(userIds))
        .then((publicIds) => {
          return this.handOverToConnector(
            this.encodeMessageBatch(messageBatchList, publicIds)
          )
        })
    }
  }

  /**
   * Give the encoded batch to the connector, and treat a refusal as a failure.
   *
   * `send()` returns a `boolean`, and `false` means the frame did not leave:
   * the WebSocket is not open, or long-polling has no publication path or no
   * `XMLHttpRequest`. Returning that boolean to the caller reproduced the very
   * defect this class was fixed for — a publish that never happened, reported
   * as a resolved promise — one layer further out.
   *
   * What it still cannot promise is DELIVERY. `send()` hands the frame to the
   * socket, and long-polling fires the request without awaiting the response,
   * so a frame accepted here can still be lost in flight. The contract is
   * "the transport took it", and the JSDoc on the public methods says so.
   */
  /**
   * The JSON-RPC adapter, or a coded rejection instead of a silent no-op.
   *
   * `_jsonRpcAdapter` is `null` until `init()` assigns it, and every publish
   * path reached it through `this._jsonRpcAdapter?.…`. On the optional-chain
   * miss an `async` method RESOLVES `undefined`: nothing encoded, no frame, no
   * error — the exact outcome 3.0.0 set out to remove, surviving on the branch
   * nobody looked at because it is only taken by push-server 5+.
   */
  private requireJsonRpcAdapter(): JsonRpc {
    if (!this._jsonRpcAdapter) {
      throw new SdkError({
        code: 'JSSDK_PULL_SEND_REFUSED',
        description: 'Pull: the JSON-RPC adapter is not ready, so the message was not sent. The client has not finished starting.',
        status: 0
      })
    }

    return this._jsonRpcAdapter
  }

  private handOverToConnector(buffer: ArrayBuffer | string): true {
    const connector = this.connector
    if (!connector) {
      throw new SdkError({
        code: 'JSSDK_PULL_SEND_REFUSED',
        description: 'Pull: there is no connector to send through — the client is not connected.',
        status: 0
      })
    }

    if (!connector.send(buffer)) {
      throw new SdkError({
        code: 'JSSDK_PULL_SEND_REFUSED',
        // No caller value is interpolated: `SdkError` descriptions are not run
        // through the log redaction. The connector has already logged the
        // specific reason.
        description: 'Pull: the connector refused the frame, so it never left — the socket is not open, or long-polling has no publication path. The connector log carries the specific reason.',
        status: 0
      })
    }

    return true
  }

  /**
   * @param messageBatchList
   * @param publicIds
   */
  private encodeMessageBatch(
    messageBatchList: TypePullClientMessageBatch[],
    publicIds: Record<number, TypeChanel>
  ): ArrayBuffer | string {
    const messages: LiteIncomingMessage[] = []

    messageBatchList.forEach((messageFields) => {
      const messageBody = messageFields.body

      let receivers: LiteReceiver[] = []
      if (messageFields.userList) {
        receivers = this.createMessageReceivers(
          messageFields.userList,
          publicIds
        )
      }

      if (messageFields.channelList) {
        if (!Type.isArray(messageFields.channelList)) {
          throw new TypeError('messageFields.publicChannels must be an array')
        }

        messageFields.channelList.forEach((publicChannel) => {
          let publicId
          let signature
          if (
            typeof publicChannel === 'string'
            && publicChannel.includes('.')
          ) {
            const fields = publicChannel.toString().split('.')
            publicId = fields[0]
            signature = fields[1]
          } else if (
            typeof publicChannel === 'object'
            && 'publicId' in publicChannel
            && 'signature' in publicChannel
          ) {
            publicId = publicChannel?.publicId
            signature = publicChannel?.signature
          } else {
            throw new Error(
              'Public channel MUST be either a string, formatted like "{publicId}.{signature}" or an object with fields \'publicId\' and \'signature\''
            )
          }

          receivers.push({
            id: this.encodeId(publicId!),
            signature: this.encodeId(signature!)
          })
        })
      }

      messages.push({
        receivers,
        body: JSON.stringify(messageBody),
        expiry: messageFields.expiry || 0
      })
    })

    // The frame is described ONCE, as plain data, and each codec serialises it.
    //
    // It used to be built with `Receiver.create` / `IncomingMessage.create` /
    // `RequestBatch.create` before the branch, which meant the lite path still
    // required the library at runtime — so opting in cost 4 kB and saved
    // nothing, and the library could not be deleted by flipping a default.
    // It also forced the codec to reproduce protobuf.js's prototype defaults
    // (`isPrivate = false`, `type = ''`), a trap that simply does not exist for
    // plain objects.
    if (this._useLiteProtobuf) {
      // A `Uint8Array`, like `.finish()` on the other branch. The declared
      // return type has always been wrong for both — see `abstract-connector`,
      // whose `send()` really accepts an `ArrayBufferView`.
      return encodeRequestBatch(messages) as unknown as ArrayBuffer
    }

    return RequestBatch.encode(RequestBatch.create({
      requests: [{ incomingMessages: { messages } }]
    })).finish()
  }

  /**
   * @param users
   * @param publicIds
   */
  private createMessageReceivers(
    users: number[],
    publicIds: Record<number, TypeChanel>
  ): LiteReceiver[] {
    const result = []

    for (const userId of users) {
      if (!publicIds[userId] || !publicIds[userId].publicId) {
        throw new Error(`Could not determine public id for user ${userId}`)
      }

      result.push({
        id: this.encodeId(publicIds[userId].publicId),
        signature: this.encodeId(publicIds[userId].signature)
      })
    }

    return result
  }

  // endregion ////

  // region _userStatusCallbacks ////
  /**
   * @param userId
   * @param isOnline
   */
  private emitUserStatusChange(userId: number, isOnline: boolean): void {
    if (this._userStatusCallbacks[userId]) {
      for (const callback of this._userStatusCallbacks[userId]) {
        callback({
          userId,
          isOnline
        })
      }
    }
  }

  private restoreUserStatusSubscription(): void {
    for (const userId in this._userStatusCallbacks) {
      if (
        Object.prototype.hasOwnProperty.call(this._userStatusCallbacks, userId)
        && this._userStatusCallbacks[userId]!.length > 0
      ) {
        this._jsonRpcAdapter?.executeOutgoingRpcCommand(
          RpcMethod.SubscribeStatusChange,
          {
            userId: userId
          }
        )
      }
    }
  }

  // endregion ////

  // region Config ////
  private async loadConfig(_logTag?: string): Promise<TypePullClientConfig> {
    if (!this._config) {
      this._config = Object.assign({}, EmptyConfig)

      let config: any
      if (this._storage) {
        config = this._storage.get(LsKeys.PullConfig, null)
      }

      if (
        this.isConfigActual(config)
        && this.checkRevision(config.api.revision_web)
      ) {
        return Promise.resolve(config)
      } else if (this._storage) {
        this._storage.remove(LsKeys.PullConfig)
      }
    } else if (
      this.isConfigActual(this._config)
      && this.checkRevision(this._config.api.revision_web)
    ) {
      return Promise.resolve(this._config)
    } else {
      this._config = Object.assign({}, EmptyConfig)
    }

    return new Promise((resolve, reject) => {
      this._restClient
        .actions.v2.call.make({
          method: this._configGetMethod,
          params: { CACHE: 'N' }
        })
        .then((response) => {
          const data = response.getData()!.result as any

          const timeShift = Math.floor(
            (Date.now() - new Date(data.serverTime).getTime()) / 1000
          )

          delete data.serverTime

          const config = Object.assign({}, data)
          config.server.timeShift = timeShift

          resolve(config)
        })
        .catch((error: unknown) => { reject(error) })
    })
  }

  /**
   * @param config
   */
  private isConfigActual(config: any): boolean {
    if (!Type.isPlainObject(config)) {
      return false
    }

    if (Number(config['server'].config_timestamp) !== this._configTimestamp) {
      return false
    }

    const now = new Date()

    if (
      Type.isNumber(config['exp'])
      && config['exp'] > 0
      && config['exp'] < now.getTime() / 1000
    ) {
      return false
    }

    const channelCount = Object.keys(config['channels']).length
    if (channelCount === 0) {
      return false
    }

    for (const channelType in config['channels']) {
      if (!Object.prototype.hasOwnProperty.call(config['channels'], channelType)) {
        continue
      }

      const channel = config['channels'][channelType]
      const channelEnd = new Date(channel.end)

      if (channelEnd < now) {
        return false
      }
    }

    return true
  }

  private startCheckConfig(): void {
    if (this._disposed) {
      return
    }

    if (this._checkInterval) {
      clearInterval(this._checkInterval)
      this._checkInterval = null
    }

    this._checkInterval = setInterval(
      this.checkConfig.bind(this),
      CONFIG_CHECK_INTERVAL
    )
  }

  private stopCheckConfig(): void {
    if (this._checkInterval) {
      clearInterval(this._checkInterval)
    }
    this._checkInterval = null
  }

  private checkConfig(): boolean {
    if (this.isConfigActual(this._config)) {
      if (!this.checkRevision(Text.toInteger(this._config?.api.revision_web))) {
        return false
      }
    } else {
      this.logToConsole('Stale config detected. Restarting')
      this.restart(CloseReasons.CONFIG_EXPIRED, 'config expired')
    }

    return true
  }

  /**
   * @param config
   * @param allowCaching
   */
  private setConfig(config: TypePullClientConfig, allowCaching: boolean): void {
    for (const key in config) {
      if (
        Object.prototype.hasOwnProperty.call(config, key)
        && Object.prototype.hasOwnProperty.call(this._config, key)
      ) {
        // @ts-expect-error this normal work - see ory code
        this._config[key] = config[key]
      }
    }

    if (config.publicChannels) {
      this.setPublicIds(Object.values(config.publicChannels))
    }

    this._configTimestamp = Number(config.server.config_timestamp)

    // Skip re-persisting once disposed: an in-flight loadConfig() from
    // start()/restart() can resolve AFTER destroy() removed the cache, which
    // would write the jwt + channel signatures back to localStorage (#242).
    if (this._storage && allowCaching && !this._disposed) {
      try {
        // Accepted shared-origin risk (#43): the cached config includes the push
        // `jwt` and channel signatures. localStorage is readable by any same-origin
        // script, so this is no weaker than the in-memory config under an
        // XSS/extension threat — and caching avoids a config round-trip on every
        // reconnect. One asymmetry: localStorage persists across reloads/tabs, so a
        // stolen value outlives the page (bounded by the short-lived, push-session-
        // tied jwt/signatures). A deliberate trade-off; documented rather than
        // stripped, and tracked for re-evaluation in #242.
        this._storage.set(LsKeys.PullConfig, config)
      } catch (error) {
        /**
         * @memotry to delete the key "history"
         * (landing site change history, see http://jabber.bx/view.php?id=136492)
         */
        if (typeof localStorage !== 'undefined' && localStorage.removeItem) {
          localStorage.removeItem('history')
        }
        this.getLogger().error(
          `${Text.getDateForLog()}: Pull: Could not cache config in local storage.`,
          { error }
        ).catch(() => {})
      }
    }
  }

  private setPublicIds(publicIds: TypePublicIdDescriptor[]): void {
    this._channelManager.setPublicIds(publicIds)
  }

  /**
   * @param serverRevision
   */
  private checkRevision(serverRevision: number): boolean {
    if (this._skipCheckRevision) {
      return true
    }

    if (serverRevision > 0 && serverRevision !== REVISION) {
      this._enabled = false
      this.showNotification('PULL_OLD_REVISION')
      this.disconnect(CloseReasons.NORMAL_CLOSURE, 'check_revision')

      this.onCustomEvent('onPullRevisionUp', [serverRevision, REVISION])

      this.emit({
        type: SubscriptionType.Revision,
        data: {
          server: serverRevision,
          client: REVISION
        }
      })

      this.logToConsole(
        `Pull revision changed from ${REVISION} to ${serverRevision}. Reload required`
      )

      return false
    }

    return true
  }

  // endregion ////

  // region Connect|ReConnect|DisConnect ////
  private disconnect(disconnectCode: number, disconnectReason: string): void {
    if (this.connector) {
      this._isManualDisconnect = true
      this.connector.disconnect(disconnectCode, disconnectReason)
    }
  }

  private restoreWebSocketConnection(): void {
    if (this._connectionType === ConnectionType.WebSocket) {
      return
    }

    this._connectors.webSocket?.connect()
  }

  /**
   * @param connectionDelay
   */
  private scheduleReconnect(connectionDelay: number = 0): void {
    if (this._disposed || !this._enabled) {
      return
    }

    if (!connectionDelay) {
      /**
       * never fallback to long polling
       * @memo remove long polling support later
       */
      /* /
      if(
        this._connectionAttempt > 3
        && this._connectionType === ConnectionType.WebSocket
        && !this._sharedConfig.isLongPollingBlocked()
      )
      {
        // Websocket seems to be closed by network filter. Trying to fall back to long polling ////
        this._sharedConfig.setWebSocketBlocked(true)
        this._connectionType = ConnectionType.LongPolling
        this._connectionAttempt = 1
        connectionDelay = 1
      }
      else
      // */
      {
        connectionDelay = this.getConnectionAttemptDelay(
          this._connectionAttempt
        )
      }
    }
    this.logToConsole(
      `Pull: scheduling reconnection in ${connectionDelay} seconds; attempt # ${this._connectionAttempt}`
    )

    this.armTimeout('_reconnectTimeout', () => {
      this.connect().catch((error) => {
        this.getLogger().error('scheduleReconnect', { error }).catch(() => {})
      })
    }, connectionDelay * 1_000)
  }

  private scheduleRestoreWebSocketConnection(): void {
    if (this._disposed) {
      return
    }

    this.logToConsole(
      `Pull: scheduling restoration of websocket connection in ${RESTORE_WEBSOCKET_TIMEOUT} seconds`
    )

    if (this._restoreWebSocketTimeout) {
      return
    }

    this.armTimeout('_restoreWebSocketTimeout', () => {
      this._restoreWebSocketTimeout = null
      this.restoreWebSocketConnection()
    }, RESTORE_WEBSOCKET_TIMEOUT * 1_000)
  }

  /**
   * @returns {Promise}
   */
  private async connect(): Promise<void> {
    if (this._disposed || !this._enabled) {
      return Promise.reject()
    }
    if (this.connector?.connected) {
      return Promise.resolve()
    }

    if (this._reconnectTimeout) {
      clearTimeout(this._reconnectTimeout)
      this._reconnectTimeout = null
    }

    this.status = PullStatus.Connecting
    this._connectionAttempt++
    return new Promise((resolve, reject) => {
      this._connectPromise = {
        resolve,
        reject
      }
      this.connector?.connect()
    })
  }

  /**
   * @param disconnectCode
   * @param disconnectReason
   * @param restartDelay
   */
  private scheduleRestart(
    disconnectCode: number,
    disconnectReason: string,
    restartDelay: number = 0
  ): void {
    if (this._disposed) {
      return
    }

    if (restartDelay < 1) {
      restartDelay = Math.ceil(Math.random() * 30) + 5
    }

    this.armTimeout(
      '_restartTimeout',
      () => this.restart(disconnectCode, disconnectReason),
      restartDelay * 1_000
    )
  }

  // endregion ////

  // region Handlers ////
  /**
   * @param messageFields
   */
  private handleRpcIncomingMessage(messageFields: any): object {
    this._session.mid = messageFields.mid
    const body = messageFields.body

    // A throw here escapes the JSON-RPC batch loop and loses every command
    // after this one (#564): skip a body that is not an object, and replace
    // an `extra` that is not one.
    if (body === null || typeof body !== 'object') {
      this.getLogger().warning(
        `${Text.getDateForLog()}: Pull: an rpc message body was not an object and was skipped`,
        { bodyType: body === null ? 'null' : typeof body }
      ).catch(() => {})
      return {}
    }
    if (body.extra === null || typeof body.extra !== 'object') {
      body.extra = {}
    }
    body.extra.sender = messageFields.sender

    // `Object.assign` onto a missing `params` throws and loses the rest of
    // the batch (#566); merge into a fresh object instead.
    const mergesParams = Type.isPlainObject(messageFields.user_params) || Type.isPlainObject(messageFields.dictionary)
    if (mergesParams && (body.params === null || typeof body.params !== 'object')) {
      body.params = {}
    }

    if (
      'user_params' in messageFields
      && Type.isPlainObject(messageFields.user_params)
    ) {
      Object.assign(body.params, messageFields.user_params)
    }

    if (
      'dictionary' in messageFields
      && Type.isPlainObject(messageFields.dictionary)
    ) {
      Object.assign(body.params, messageFields.dictionary)
    }

    if (this.checkDuplicate(messageFields.mid)) {
      this.addMessageToStat(body)
      this.trimDuplicates()
      this.broadcastMessage(body)
    }

    this.connector?.send(`mack:${messageFields.mid}`)

    return {}
  }

  /**
   * @param events
   */
  private handleIncomingEvents(events: TypeSessionEvent[]): void {
    const messages: TypePullClientMessageBody[] = []
    if (events.length === 0) {
      this._session.mid = null
      return
    }

    for (const event of events) {
      this.updateSessionFromEvent(event)

      if (event.mid && !this.checkDuplicate(event.mid)) {
        continue
      }

      this.addMessageToStat(
        event.text as { module_id: string, command: string }
      )
      messages.push(event.text as TypePullClientMessageBody)
    }
    this.trimDuplicates()
    this.broadcastMessages(messages)
  }

  /**
   * @param event
   */
  private updateSessionFromEvent(event: TypeSessionEvent): void {
    this._session.mid = event.mid || null
    this._session.tag = event.tag || null
    this._session.time = event.time || null
  }

  /**
   * @process
   *
   * @param command
   * @param message
   */
  private handleInternalPullEvent(
    command: string,
    message: TypePullClientMessageBody
  ): void {
    switch (command.toUpperCase()) {
      case SystemCommands.CHANNEL_EXPIRE: {
        if (message.params.action === 'reconnect') {
          const typeChanel = message.params?.channel.type as string
          if (typeChanel === 'private' && this._config?.channels?.private) {
            this._config.channels.private = message.params.new_channel
            this.logToConsole(
              `Pull: new config for ${message.params.channel.type} channel set: [updated]`
            )
          }
          if (typeChanel === 'shared' && this._config?.channels?.shared) {
            this._config.channels.shared = message.params.new_channel
            this.logToConsole(
              `Pull: new config for ${message.params.channel.type} channel set: [updated]`
            )
          }

          this.reconnect(CloseReasons.CONFIG_REPLACED, 'config was replaced')
        } else {
          this.restart(CloseReasons.CHANNEL_EXPIRED, 'channel expired received')
        }
        break
      }
      case SystemCommands.CONFIG_EXPIRE: {
        this.restart(CloseReasons.CONFIG_EXPIRED, 'config expired received')
        break
      }
      case SystemCommands.SERVER_RESTART: {
        this.reconnect(
          CloseReasons.SERVER_RESTARTED,
          'server was restarted',
          15
        )
        break
      }
      default:
    }
  }

  // region Handlers For Message ////
  /**
   * @param response
   */
  private onIncomingMessage(response: string | ArrayBuffer): void {
    if (this.isJsonRpc()) {
      if (response === JSON_RPC_PING) {
        this.onJsonRpcPing()
      } else {
        this._jsonRpcAdapter?.parseJsonRpcMessage(response as string)
      }
    } else {
      const events = this.extractMessages(response)
      this.handleIncomingEvents(events)
    }
  }

  // region onLongPolling ////
  private onLongPollingOpen(): void {
    this._unloading = false
    this._starting = false
    this._connectionAttempt = 0
    this._isManualDisconnect = false
    this.status = PullStatus.Online

    this.logToConsole('Pull: Long polling connection with push-server opened')
    if (this.isWebSocketEnabled()) {
      this.scheduleRestoreWebSocketConnection()
    }
    if (this._connectPromise) {
      this._connectPromise.resolve({})
    }
  }

  /**
   * @param response
   */
  private onLongPollingDisconnect(response: {
    code: number
    reason: string
  }): void {
    if (this._connectionType === ConnectionType.LongPolling) {
      this.status = PullStatus.Offline
    }

    this.logToConsole(
      `Pull: Long polling connection with push-server closed. Code: ${response.code}, reason: ${response.reason}`
    )
    if (!this._isManualDisconnect) {
      this.scheduleReconnect()
    }
    this._isManualDisconnect = false
    this.clearPingWaitTimeout()
  }

  /**
   * @param error
   */
  private onLongPollingError(error: Error): void {
    this._starting = false
    if (this._connectionType === ConnectionType.LongPolling) {
      this.status = PullStatus.Offline
    }

    this.getLogger().error(
      `${Text.getDateForLog()}: Pull: Long polling connection error`,
      { error }
    ).catch(() => {})

    this.scheduleReconnect()
    if (this._connectPromise) {
      this._connectPromise.reject(error)
    }

    this.clearPingWaitTimeout()
  }

  // endregion ////

  // region onWebSocket ////
  /**
   * @param response
   */
  private onWebSocketBlockChanged(response: {
    isWebSocketBlocked: boolean
  }): void {
    const isWebSocketBlocked = response.isWebSocketBlocked

    if (
      isWebSocketBlocked
      && this._connectionType === ConnectionType.WebSocket
      && !this.isConnected()
    ) {
      if (this._reconnectTimeout) {
        clearTimeout(this._reconnectTimeout)
        this._reconnectTimeout = null
      }

      this._connectionAttempt = 0
      this._connectionType = ConnectionType.LongPolling
      this.scheduleReconnect(1)
    } else if (
      !isWebSocketBlocked
      && this._connectionType === ConnectionType.LongPolling
    ) {
      if (this._reconnectTimeout) {
        clearTimeout(this._reconnectTimeout)
        this._reconnectTimeout = null
      }
      if (this._restoreWebSocketTimeout) {
        clearTimeout(this._restoreWebSocketTimeout)
        this._restoreWebSocketTimeout = null
      }

      this._connectionAttempt = 0
      this._connectionType = ConnectionType.WebSocket
      this.scheduleReconnect(1)
    }
  }

  private onWebSocketOpen(): void {
    this._unloading = false
    this._starting = false
    this._connectionAttempt = 0
    this._isManualDisconnect = false
    this.status = PullStatus.Online
    this._sharedConfig.setWebSocketBlocked(false)

    // to prevent fallback to long polling in case of networking problems
    this._sharedConfig.setLongPollingBlocked(true)

    if (this._connectionType == ConnectionType.LongPolling) {
      this._connectionType = ConnectionType.WebSocket
      this._connectors.longPolling?.disconnect(
        CloseReasons.CONFIG_REPLACED,
        'Fire at onWebSocketOpen'
      )
    }

    if (this._restoreWebSocketTimeout) {
      clearTimeout(this._restoreWebSocketTimeout)
      this._restoreWebSocketTimeout = null
    }
    this.logToConsole('Pull: Websocket connection with push-server opened')
    if (this._connectPromise) {
      this._connectPromise.resolve({})
    }

    this.restoreUserStatusSubscription()
  }

  /**
   * @param response
   */
  private onWebSocketDisconnect(response: {
    code: number
    reason: string
  }): void {
    if (this._connectionType === ConnectionType.WebSocket) {
      this.status = PullStatus.Offline
    }

    this.logToConsole(
      `Pull: Websocket connection with push-server closed. Code: ${response.code}, reason: ${response.reason}`,
      true
    )
    if (!this._isManualDisconnect) {
      if (response.code == CloseReasons.WRONG_CHANNEL_ID) {
        this.scheduleRestart(
          CloseReasons.WRONG_CHANNEL_ID,
          'wrong channel signature'
        )
      } else {
        this.scheduleReconnect()
      }
    }

    // to prevent fallback to long polling in case of networking problems
    this._sharedConfig.setLongPollingBlocked(true)
    this._isManualDisconnect = false

    this.clearPingWaitTimeout()
  }

  /**
   * @param error
   */
  private onWebSocketError(error: Error): void {
    this._starting = false
    if (this._connectionType === ConnectionType.WebSocket) {
      this.status = PullStatus.Offline
    }

    this.getLogger().error(
      `${Text.getDateForLog()}: Pull: WebSocket connection error`,
      { error }
    ).catch(() => {})
    this.scheduleReconnect()
    if (this._connectPromise) {
      this._connectPromise.reject(error)
    }

    this.clearPingWaitTimeout()
  }

  // endregion ////
  // endregion ////

  // endregion ////

  // region extractMessages ////
  /**
   * @param pullEvent
   */
  private extractMessages(pullEvent: string | ArrayBuffer): TypeSessionEvent[] {
    if (pullEvent instanceof ArrayBuffer) {
      return this.extractProtobufMessages(pullEvent)
    } else if (Type.isStringFilled(pullEvent)) {
      return this.extractPlainTextMessages(pullEvent)
    }

    throw new Error('Error pullEvent type')
  }

  /**
   * @param pullEvent
   */
  private extractProtobufMessages(pullEvent: ArrayBuffer): TypeSessionEvent[] {
    const result = []

    try {
      const responseBatch = this._useLiteProtobuf
        ? decodeResponseBatch(new Uint8Array(pullEvent))
        : ResponseBatch.decode(new Uint8Array(pullEvent))

      // The lite codec decodes a damaged frame as far as it can read instead
      // of rejecting it, which can keep sound messages the damage did not
      // touch — but it removed the only sign a frame was broken. This puts it
      // back: a push server or a proxy mangling frames should be visible to
      // whoever watches this log. Only the byte length — the frame may carry
      // other applications' events and must not be logged (#43).
      //
      // Note what "damaged" does NOT cover: a length prefix inflated to a value
      // that still fits the buffer reads the next message as part of this one
      // and is not detectable at all. See `pull-protobuf.md`.
      if ('damaged' in responseBatch && responseBatch.damaged) {
        this.getLogger().warning(
          `${Text.getDateForLog()}: Pull: a protobuf frame was damaged; whatever could not be read was dropped`,
          { byteLength: pullEvent.byteLength }
        ).catch(() => {})
      }

      for (let i = 0; i < responseBatch.responses.length; i++) {
        const response = responseBatch.responses[i]
        if (response.command !== 'outgoingMessages') {
          continue
        }

        const messages = response.outgoingMessages.messages
        for (const message of messages) {
          let messageFields
          try {
            messageFields = JSON.parse(message.body)
          } catch (error) {
            this.getLogger().error(
              `${Text.getDateForLog()}: Pull: Could not parse message body`,
              { error }
            ).catch(() => {})
            continue
          }

          // Valid JSON that is not an object — `null`, a number, a string, a
          // boolean. Reading `.extra` off `null`, or assigning it onto a
          // primitive under strict mode, threw inside the batch-wide `try`,
          // and dropped this message and every one after it (#562). Skipped,
          // and logged without the body (#43). Arrays are objects and pass.
          if (messageFields === null || typeof messageFields !== 'object') {
            this.getLogger().warning(
              `${Text.getDateForLog()}: Pull: a message body was not a JSON object and was skipped`,
              { responseIndex: i, bodyType: messageFields === null ? 'null' : typeof messageFields }
            ).catch(() => {})
            continue
          }

          // A primitive `extra` would make the `sender` assignment below
          // throw and drop the rest of the frame (#564); replace it.
          if (messageFields.extra === null || typeof messageFields.extra !== 'object') {
            messageFields.extra = {}
          }
          // A message with no `sender` is SKIPPED, not repaired — and logged.
          //
          // `sender.type` decides who a message is for: `broadcastMessage`
          // routes `Client` to client subscribers and everything else to
          // server subscribers — or, with `module_id: 'pull'`, to the internal
          // command handler. Filling in `Unknown` for a sender that could not
          // be read would deliver a client-published message as a SERVER one,
          // across that trust boundary. Losing it is the safer failure.
          //
          // Skipping it here, rather than letting `.type` throw, keeps the
          // rest of the batch: that read sat inside the batch-wide `try`, so
          // one missing sender used to drop it and every message after it. It
          // did so in both codecs — protobuf.js puts `sender = null` on the
          // prototype, so a sender-less message threw there too.
          //
          // Expected to fire only on damage, and that is an inference, not an
          // observation: an audit of the push-server sources reports it sets
          // `sender` on every message it relays, and the unguarded read above
          // shipped for years without such losses being reported. No recorded
          // frame carries a `pull` or `online` system command, so it is not
          // confirmed for those. Hence the warning: if the inference is ever
          // wrong, a silently skipped CHANNEL_EXPIRE would look like a dead
          // connection with nothing in the log to say why.
          if (!message.sender) {
            this.getLogger().warning(
              `${Text.getDateForLog()}: Pull: a protobuf message arrived with no sender and was skipped`,
              { responseIndex: i, moduleId: typeof messageFields.module_id === 'string' ? messageFields.module_id : null }
            ).catch(() => {})
            continue
          }

          messageFields.extra.sender = {
            type: message.sender.type
          }

          if (message.sender.id instanceof Uint8Array) {
            messageFields.extra.sender.id = this.decodeId(message.sender.id)
          }

          const compatibleMessage = {
            mid: this.decodeId(message.id),
            text: messageFields
          }

          result.push(compatibleMessage)
        }
      }
    } catch (error) {
      this.getLogger().error(
        `${Text.getDateForLog()}: Pull: Could not parse message`,
        { error }
      ).catch(() => {})
    }

    return result
  }

  /**
   * @param pullEvent
   */
  private extractPlainTextMessages(pullEvent: string): TypeSessionEvent[] {
    const result = []

    const dataArray = pullEvent.match(/#!NGINXNMS!#(.*?)#!NGINXNME!#/g)
    if (dataArray === null) {
      this.getLogger().warning('PULL ERROR', {
        errorType: 'parseResponse error parsing message',
        // The frame failed the NGINX-delimiter match, so its content is
        // unparseable anyway and could carry a credential (e.g. a channel
        // `signature`); log only its size, never the bytes (#43).
        byteLength: pullEvent.length
      }).catch(() => {})

      return []
    }
    for (let i = 0; i < dataArray.length; i++) {
      dataArray[i] = dataArray[i]!.substring(12, dataArray[i]!.length - 12)
      if (dataArray[i]!.length <= 0) {
        continue
      }

      let data
      try {
        data = JSON.parse(dataArray[i]!)
      } catch {
        continue
      }

      result.push(data as TypeSessionEvent)
    }

    return result
  }

  /**
   * Converts message id from byte[] to string
   * @param {Uint8Array} encodedId
   * @return {string}
   */
  private decodeId(encodedId: Uint8Array): string {
    let result = ''
    for (const element_ of encodedId) {
      const hexByte = element_.toString(16)
      if (hexByte.length === 1) {
        result += '0'
      }
      result += hexByte
    }

    return result
  }

  /**
   * Converts message id from hex-encoded string to byte[]
   * @param {string} id Hex-encoded string.
   * @return {Uint8Array}
   */
  private encodeId(id: string): Uint8Array {
    if (!id) {
      return new Uint8Array()
    }

    const result = []
    for (let i = 0; i < id.length; i += 2) {
      result.push(Number.parseInt(id.slice(i, i + 2), 16))
    }

    return new Uint8Array(result)
  }

  // endregion ////

  // region Events.Status /////
  private onOffline(): void {
    if (this._disposed) {
      return
    }
    this.disconnect(CloseReasons.NORMAL_CLOSURE, 'offline')
  }

  private onOnline(): void {
    if (this._disposed) {
      return
    }
    this.connect().catch((error) => {
      this.getLogger().error('onOnline', { error }).catch(() => {})
    })
  }

  private onBeforeUnload(): void {
    this._unloading = true

    this.persistSession()

    this.scheduleReconnect(15)
  }

  // Persist the current session (with a short TTL) so a quick reload / re-init can
  // resume it. Shared by onBeforeUnload and destroy() — destroy() saves WITHOUT
  // scheduling a reconnect (#141).
  private persistSession(): void {
    const session = Type.clone(this.session)
    session.ttl = Date.now() + LS_SESSION_CACHE_TIME * 1000
    if (this._storage) {
      try {
        this._storage.set(
          LS_SESSION,
          JSON.stringify(session)
          // LS_SESSION_CACHE_TIME
        )
      } catch (error) {
        this.getLogger().error(
          `${Text.getDateForLog()}: Pull: Could not save session info in local storage. Error: `,
          { error }
        ).catch(() => {})
      }
    }
  }

  // endregion ////

  // region PullStatus ////
  /**
   * @param status
   * @param delay
   */
  private sendPullStatusDelayed(status: PullStatus, delay: number): void {
    if (this._disposed) {
      return
    }

    this.armTimeout('_offlineTimeout', () => {
      this._offlineTimeout = null
      this.sendPullStatus(status)
    }, delay)
  }

  /**
   * @param status
   */
  private sendPullStatus(status: PullStatus): void {
    if (this._unloading) {
      return
    }

    this.onCustomEvent('onPullStatus', [status])

    this.emit({
      type: SubscriptionType.Status,
      data: {
        status: status
      }
    })
  }

  // endregion ////

  // region _watchTagsQueue ////
  /**
   * @memo if private?
   * @param tagId
   * @param force
   */
  // @ts-expect-error When we rewrite it to something more modern, then we'll remove this
  private extendWatch(tagId: string, force: boolean = false): void {
    if (this._watchTagsQueue.get(tagId)) {
      return
    }

    this._watchTagsQueue.set(tagId, true)
    if (force) {
      this.updateWatch(force)
    }
  }

  /**
   * @param force
   */
  private updateWatch(force: boolean = false): void {
    if (this._disposed) {
      return
    }

    this.armTimeout(
      '_watchUpdateTimeout',
      () => {
        /**
         * @memo test this
         */
        const watchTags = [...this._watchTagsQueue.keys()]

        if (watchTags.length > 0) {
          this._restClient
            .actions.v2.call.make({
              method: 'pull.watch.extend',
              params: { tags: watchTags }
            })
            .then((response: AjaxResult) => {
              /**
               * @memo test this
               */
              const updatedTags: NumberString[] = (response.getData() as SuccessPayload<NumberString[]>).result as any

              for (const tagId of updatedTags) {
                this.clearWatch(tagId)
              }

              this.updateWatch()
            })
            .catch(() => {
              this.updateWatch()
            })
        } else {
          this.updateWatch()
        }
      },
      force ? this._watchForceUpdateInterval : this._watchUpdateInterval
    )
  }

  /**
   * @param tagId
   */
  private clearWatch(tagId: string): void {
    this._watchTagsQueue.delete(tagId)
  }

  // endregion ////

  // region Ping ////
  private onJsonRpcPing(): void {
    this.updatePingWaitTimeout()
    this.connector?.send(JSON_RPC_PONG)
  }

  private updatePingWaitTimeout(): void {
    if (this._disposed) {
      return
    }

    this.armTimeout(
      '_pingWaitTimeout',
      this._onPingTimeoutHandler,
      PING_TIMEOUT * 2 * 1_000
    )
  }

  private clearPingWaitTimeout(): void {
    if (this._pingWaitTimeout) {
      clearTimeout(this._pingWaitTimeout)
    }

    this._pingWaitTimeout = null
  }

  private onPingTimeout(): void {
    this._pingWaitTimeout = null
    if (this._disposed || !this._enabled || !this.isConnected()) {
      return
    }

    this.getLogger().warning(`No pings are received in ${PING_TIMEOUT * 2} seconds. Reconnecting`).catch(() => {})
    this.disconnect(CloseReasons.STUCK, 'connection stuck')

    this.scheduleReconnect()
  }

  // endregion ////

  // region Time ////
  /**
   * Returns reconnect delay in seconds
   *
   * @param attemptNumber
   * @return {number}
   */
  private getConnectionAttemptDelay(attemptNumber: number): number {
    let result
    if (attemptNumber < 1) {
      result = 0.5
    } else if (attemptNumber < 3) {
      result = 15
    } else if (attemptNumber < 5) {
      result = 45
    } else if (attemptNumber < 10) {
      result = 600
    } else {
      result = 3_600
    }

    return result + result * Math.random() * 0.2
  }

  // endregion ////

  // region Tools ////
  /**
   * @param mid
   */
  private checkDuplicate(mid: string): boolean {
    if (this._session.lastMessageIds.includes(mid)) {
      this.getLogger().warning(`Duplicate message ${mid} skipped`).catch(() => {})
      return false
    } else {
      this._session.lastMessageIds.push(mid)
      return true
    }
  }

  private trimDuplicates(): void {
    if (this._session.lastMessageIds.length > MAX_IDS_TO_STORE) {
      this._session.lastMessageIds
        = this._session.lastMessageIds.slice(-MAX_IDS_TO_STORE)
    }
  }

  // endregion ////

  // region Logging ////
  /**
   * @param message
   */
  private logMessage(message: TypePullClientMessageBody): void {
    if (!this._debug) {
      return
    }

    if (
      message.extra?.sender
      && message.extra.sender.type === SenderType.Client
    ) {
      this.getLogger().info(
        `onPullClientEvent-${message.module_id}`, redactSensitiveParams({
          command: message.command,
          params: message.params,
          extra: message.extra
        })
      ).catch(() => {})
    } else if (message.module_id == 'online') {
      this.getLogger().info(
        `onPullOnlineEvent`, redactSensitiveParams({
          command: message.command,
          params: message.params,
          extra: message.extra
        })
      ).catch(() => {})
    } else {
      this.getLogger().info(
        `onPullEvent`,
        redactSensitiveParams({
          moduleId: message.module_id,
          command: message.command,
          params: message.params,
          extra: message.extra
        })
      ).catch(() => {})
    }
  }

  /**
   * @param message
   * @param force
   */
  private logToConsole(message: string, force: boolean = false): void {
    if (this._loggingEnabled || force) {
      this.getLogger().debug(`${Text.getDateForLog()}: ${message}`).catch(() => {})
    }
  }

  /**
   * @param message
   */
  private addMessageToStat(message: {
    module_id: string
    command: string
  }): void {
    if (!this._session.history[message.module_id]) {
      this._session.history[message.module_id] = {}
    }
    if (!this._session.history[message.module_id][message.command]) {
      this.session.history[message.module_id][message.command] = 0
    }

    this._session.history[message.module_id][message.command]++

    this._session.messageCount++
  }

  /**
   * @param text
   */
  private showNotification(text: string): void {
    this.getLogger().notice(text).catch(() => {})

    /* /
    if(this._notificationPopup || typeof BX.PopupWindow === 'undefined')
    {
      return;
    }

    this._notificationPopup = new BX.PopupWindow('bx-notifier-popup-confirm', null, {
      zIndex: 200,
      autoHide: false,
      closeByEsc: false,
      overlay: true,
      content: BX.create("div", {
        props: {className: "bx-messenger-confirm"},
        html: text
      }),
      buttons: [
        new BX.PopupWindowButton({
          text: BX.message('JS_CORE_WINDOW_CLOSE'),
          className: "popup-window-button-decline",
          events: {
            click: () => this._notificationPopup.close(),
          }
        })
      ],
      events: {
        onPopupClose: () => this._notificationPopup.destroy(),
        onPopupDestroy: () => this._notificationPopup = null,
      }
    });
    this._notificationPopup.show();
    // */
  }

  // endregion ////

  // region onCustomEvent ////
  /**
   * @memo may be need to use onCustomEvent
   * @memo ? force
   */
  private onCustomEvent(
    eventName: string,
    data: any,
    force: boolean = false
  ): void {
    if (eventName || data || force) {
      /* empty */
    }

    /* /
    if (BX && BX.onCustomEvent)
    {
      BX.onCustomEvent(window, eventName, data, force)
    }
    // */
  }
  // endregion ////
}
